/**
 * useRegistrationForm — Multi-step registration wizard
 *
 * Handles form state, validation, partner email check,
 * step navigation, handicap auto-check, and submission
 * via registration-ops API.
 *
 * Handicap flow (matches old FastAPI behavior):
 *   - After position is selected, auto-checks if user has a handicap on file
 *     for the event's org + position.
 *   - If yes → shows the existing value (read-only), no input needed.
 *   - If no → prompts user to enter a numeric handicap value (0-15)
 *     which is saved as user-entered and pending admin approval.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useCreateRegistration,
  useRegisterWithPaymentDeferred,
  useRegisterWithCash,
  useCompleteRegistrationPayment,
  useCancelRegistrationPayment,
  useAbandonRegistrationPayment,
  useEventCapacity,
  useHandicapCheck,
  useRegistrationPolicy,
  useOutstandingBalance,
  type PaymentRegistrationResponse,
} from '../api/extensions/useRegistrationOps';
import { usePartnerFeePreview } from '../api/extensions/usePartnerSearch';
import { useStripeFeeConfig } from '../api/extensions/useStripeFeeConfig';
import { calculateTransactionFee } from '../utils/stripe-fee-calculator';
import { isTeamRopingEvent } from '../utils/eventTypes';
import { resolveRegistrationPaymentVisibility } from '../utils/registrationPaymentOptions';

// --- Types ---

export type RegistrationStep = 'partner' | 'performance' | 'handicap' | 'payment' | 'review';

const ALL_STEPS: RegistrationStep[] = ['partner', 'performance', 'handicap', 'payment', 'review'];

interface RegistrationFormData {
  event_id: string;
  position: 'Header' | 'Heeler' | '';
  registration_type: 'individual' | 'team_primary' | 'team_partner';
  partner_email: string;
  partner_user_profile_id: string;
  buddy_group_id: string;
  buddy_group_position: number | null;
  /**
   * 'stripe' = online pay (card + ACH via Stripe Elements). 'cash' = pay
   * organizer in person. 'pay_later' = members-only deferred pay. The legacy
   * 'paypal' value is retained as a synonym for 'stripe' so older form state
   * and test fixtures keep working — the backend coerces both to Stripe.
   */
  payment_method: 'stripe' | 'paypal' | 'cash' | 'pay_later' | '';
  paid_for_partner: boolean;
  notes: string;
  manual_handicap_value: number | null;
  first_performance_preference: string;
  second_performance_preference: string;
  /** Association org ID for card-based priority lookup */
  card_member_org_id: string;
  /** User profile ID of the card-holding member */
  card_member_user_id: string;
  /**
   * Three-path non-member choice. Empty when the user is a confirmed member
   * or when the org doesn't charge a member fee — in either case the
   * registration UI does not show the choice panel.
   */
  non_member_choice: '' | 'pay_non_member' | 'claim_member' | 'member';
  /** Card / member number entered on the trust path. */
  claimed_member_number: string;
  /**
   * On-behalf registration target. When non-empty AND different from the
   * actor's profile id, the registration is created for this user instead
   * of the actor. Only the buddy-group primary cardholder may set this,
   * and the backend re-authorizes on every submit.
   */
  target_user_profile_id: string;
}

interface ValidationErrors {
  position?: string;
  partner_email?: string;
  event_id?: string;
  payment_method?: string;
  manual_handicap_value?: string;
  first_performance_preference?: string;
  non_member_choice?: string;
  claimed_member_number?: string;
}

interface EventInfo {
  entry_fee?: number | null;
  transaction_fee?: number | null;
  max_combined_handicap?: number | null;
  max_individual_handicap?: number | null;
  header_max_handicap?: number | null;
  heeler_max_handicap?: number | null;
  /**
   * Discriminator from the parent rodeo row (rodeo_or_jackpot column).
   * Set by the caller (e.g. EventDetailPage merges it into the event prop)
   * so the wizard can hide handicap-only steps and the registration payload
   * can omit handicap fields when this is `'rodeo'`. Handicaps apply to
   * jackpots only — see #remove-handicaps-from-rodeos task.
   */
  rodeo_or_jackpot?: string | null;
  [key: string]: unknown;
}

export function useRegistrationForm(eventId: string, eventInfo?: EventInfo) {
  // --- Policy & balance via React Query ---
  //
  // Both pieces of data live under `registrationOpsKeys.all` so any
  // registration mutation (create, cancel, capture, etc.) invalidates them
  // automatically. This is what fixes the cancel → re-register stale-state
  // bug: previously we fetched both inside a `useEffect` keyed only on
  // `eventId`, so the modal kept its old "payment pending" / unpaid-balance
  // state across the cancel + reopen cycle until the page was refreshed.
  const { data: policyData = null } = useRegistrationPolicy(eventId);
  const { data: balanceData } = useOutstandingBalance();

  /**
   * Server-side outstanding balance. Derived directly from the query so it
   * stays in lock-step with cache invalidation; no local mirror state.
   */
  const totalOutstanding = Number(balanceData?.total_outstanding ?? 0);
  const hasServerUnpaidBalance = totalOutstanding > 0;
  const unpaidEvents = balanceData?.unpaid_events ?? [];

  /**
   * 402 override: when POST /register returns UNPAID_BALANCE the response
   * carries the authoritative `unpaid_events` list for the moment of the
   * attempt — scoped to the organization that owns the rodeo the user
   * was trying to register for. We surface that override until the next
   * successful query refresh (or an explicit `reset()`) so the user sees
   * exactly which events the backend wants them to settle for THIS org.
   */
  interface OrgUnpaidBlock {
    organization_id: string | null;
    organization_name: string | null;
    unpaid_events: Array<{
      registration_id?: string;
      event_id: string;
      event_name: string | null;
      organization_name?: string | null;
      amount_owed: number;
    }>;
  }
  const [orgUnpaidBlock, setOrgUnpaidBlock] = useState<OrgUnpaidBlock | null>(
    null,
  );

  // Clear the 402 override once the underlying balance query refreshes —
  // otherwise the modal would re-open even after the user pays the
  // outstanding fees in another tab.
  useEffect(() => {
    if (orgUnpaidBlock !== null) {
      setOrgUnpaidBlock(null);
    }
    // We intentionally only re-run this when the server-side balance data
    // changes. `orgUnpaidBlock` is a setter target, not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceData]);

  /**
   * `hasUnpaidBalance` retains its previous semantics — it reflects the
   * user's GLOBAL balance — purely so existing UI surfaces (the funds
   * page deep-link, the header indicator) keep working unchanged. The
   * registration gate itself is per-org and lives on `orgUnpaidBlock`.
   */
  const hasUnpaidBalance = hasServerUnpaidBalance;
  const unpaidFeeBlock = hasServerUnpaidBalance ? unpaidEvents : null;


  /**
   * Which payment options this event actually offers, and to whom. The
   * backend has already resolved org -> rodeo -> event inheritance; this
   * turns its answers into the flags the UI renders from.
   */
  const {
    cashAllowed,
    payLaterAllowed,
    showMembershipCard: showMembershipChoice,
  } = resolveRegistrationPaymentVisibility(policyData);

  // Compute max handicap for the selected position
  const getMaxHandicap = (pos: string): number | null => {
    const posLower = pos.toLowerCase();
    // Position-specific max takes priority
    if (posLower === 'header' && eventInfo?.header_max_handicap != null) {
      return eventInfo.header_max_handicap;
    }
    if (posLower === 'heeler' && eventInfo?.heeler_max_handicap != null) {
      return eventInfo.heeler_max_handicap;
    }
    // Fall back to individual max, then combined max
    if (eventInfo?.max_individual_handicap != null) return eventInfo.max_individual_handicap;
    if (eventInfo?.max_combined_handicap != null) return eventInfo.max_combined_handicap;
    return null;
  };

  // Form data
  const [formData, setFormData] = useState<RegistrationFormData>({
    event_id: eventId,
    position: '',
    registration_type: 'individual',
    partner_email: '',
    partner_user_profile_id: '',
    buddy_group_id: '',
    buddy_group_position: null,
    payment_method: '',
    paid_for_partner: false,
    notes: '',
    manual_handicap_value: null,
    first_performance_preference: '',
    second_performance_preference: '',
    card_member_org_id: '',
    card_member_user_id: '',
    non_member_choice: '',
    claimed_member_number: '',
    target_user_profile_id: '',
  });

  /**
   * Buddy-group + performance-preference wire fields.
   *
   * A finals rodeo seats every contestant in every performance, so neither
   * concept applies there — the fields are omitted entirely rather than sent
   * empty. The backend drops them too (a stale client must still be able to
   * enter); this keeps the payload honest.
   */
  const buddyAndPreferencePayload = useMemo(() => {
    if (policyData?.is_finals === true) return {};
    return {
      buddy_group_id: formData.buddy_group_id || undefined,
      buddy_group_position: formData.buddy_group_position ?? undefined,
      ...(formData.first_performance_preference
        ? { first_performance_preference: formData.first_performance_preference }
        : {}),
      ...(formData.second_performance_preference
        ? { second_performance_preference: formData.second_performance_preference }
        : {}),
    };
  }, [
    policyData?.is_finals,
    formData.buddy_group_id,
    formData.buddy_group_position,
    formData.first_performance_preference,
    formData.second_performance_preference,
  ]);

  // Surcharge is only billed when the contestant explicitly picks
  // 'pay_non_member'. Trust-path claims (claim_member) skip the surcharge
  // until / unless an admin rejects them — in which case the backend
  // re-applies the fee on rejection.
  const surcharge =
    showMembershipChoice && formData.non_member_choice === 'pay_non_member'
      ? policyData!.non_member_surcharge
      : 0;
  // Resolve the transaction fee using the same fallback chain as the
  // backend's PaymentService.getEffectiveTransactionFee: honor a per-event
  // override when set, otherwise derive from the platform Stripe fee config
  // so auto-mode events (transaction_fee = 0/NULL) display the same fee
  // Stripe will actually charge — without this the wizard total dropped the
  // transaction fee and disagreed with the Stripe payment sheet.
  //
  // Include the non-member surcharge in the basis so the fee covers the
  // FULL captured amount. When the contestant elects to pay the
  // non-member fee, Stripe applies its `pct * gross + fixed` charge to
  // entry + surcharge together — calculating the fee on entry alone left
  // the platform short by the surcharge's slice of the percentage rate,
  // and the in-wizard preview disagreed with the Stripe payment sheet at
  // checkout time.
  const entryFee = Number(eventInfo?.entry_fee) || 0;
  const storedTxnFee = Number(eventInfo?.transaction_fee) || 0;
  const { data: stripeFeeConfig } = useStripeFeeConfig();
  const autoTxnFee = stripeFeeConfig
    ? calculateTransactionFee(entryFee + surcharge, stripeFeeConfig)
    : 0;
  const effectiveTransactionFee = storedTxnFee > 0 ? storedTxnFee : autoTxnFee;
  const totalFee = entryFee + effectiveTransactionFee + surcharge;
  const isFreeEvent = totalFee <= 0;

  // Look up the partner's actual per-side cost so the wizard's
  // "Pay for partner" checkbox label and the review-step "Total due"
  // line reflect the partner's real membership status. Without this
  // we'd display 2× the primary's fee, then surprise the user when
  // Stripe Elements opened with a different amount because the
  // partner is (or isn't) a member of the rodeo's org. The lookup
  // only fires once a partner is actually picked from the search; the
  // hook handles its own enabled-gate so we don't need a guard here.
  const { data: partnerFeePreview = null } = usePartnerFeePreview(
    eventId,
    formData.partner_user_profile_id || null,
  );
  // Partner's per-side total used by Pay-for-partner UX. Falls back
  // to the primary's totalFee when the preview hasn't loaded yet (or
  // when no partner is selected) so the existing "× 2" display path
  // doesn't suddenly show $0.
  const partnerTotalFee =
    partnerFeePreview && formData.partner_user_profile_id
      ? partnerFeePreview.total_fee
      : totalFee;

  // Look up the target's actual membership status when registering on
  // behalf of a buddy-group member. The endpoint is membership-aware
  // and works for any user_profile_id (despite its partner-flavored
  // name), so the review step can hide the "non-member fee" hint when
  // the target is in fact a member instead of always showing
  // "Possible…" speculatively.
  const { data: targetFeePreview = null } = usePartnerFeePreview(
    eventId,
    formData.target_user_profile_id || null,
  );

  // Header/Heeler is a team-roping concept. Solo disciplines hide the role
  // selector; the partner step is also skipped because partner pairing only
  // applies to team roping.
  const isTeamRoping = isTeamRopingEvent(eventInfo);

  // Handicaps are jackpot-only. The wizard drops the entire handicap step
  // for rodeo events and the submit payload omits manual_handicap_value
  // regardless of stale form state. Backend enforces the same rule.
  const isRodeoEvent = (eventInfo as any)?.rodeo_or_jackpot === 'rodeo';

  // Determine which steps to include:
  // - Always include 'partner' — even for solo disciplines this is the
  //   first step, where the (always-present) 1st/2nd performance
  //   preferences live. Position / partner sub-sections inside the step
  //   render only for team roping.
  // - Drop 'performance' as a standalone step — its picker now renders
  //   inline at the bottom of the 'partner' step in compact mode so the
  //   user fills out position/partner/preferences in a single page.
  // - Skip 'payment' for free events (but keep it if the non-member
  //   membership-choice picker still has to render somewhere)
  // - Skip 'handicap' on rodeo events (jackpot-only feature)
  // - Skip 'handicap' on on-behalf registrations: the /handicap-check
  //   endpoint resolves to the *actor's* handicap, not the target's, so
  //   showing it here would be misleading. The backend will pull the
  //   target's handicap from `handicaps` automatically (or leave the
  //   registration without one if the target doesn't have one yet).
  // - Skip 'payment' on on-behalf: the membership-choice panel reflects
  //   the *actor's* membership status (not the target's) and the payment
  //   method picker doesn't apply because the target picks their own
  //   payment when they settle the balance later. The submit path
  //   defaults to pay_later so the spot is held for the target.
  const isOnBehalfReg = !!formData.target_user_profile_id;
  const steps: RegistrationStep[] = ALL_STEPS.filter((s) => {
    if (s === 'performance') return false;
    if (s === 'payment' && isFreeEvent && !showMembershipChoice) return false;
    if (s === 'handicap' && isRodeoEvent) return false;
    if (s === 'handicap' && isOnBehalfReg) return false;
    if (s === 'payment' && isOnBehalfReg) return false;
    return true;
  });

  // Step navigation
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = steps[currentStepIndex];

  // Validation
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // API hooks
  const createRegistration = useCreateRegistration();
  const registerWithPayment = useRegisterWithPaymentDeferred();
  const registerWithCash = useRegisterWithCash();
  const completePayment = useCompleteRegistrationPayment();
  const cancelPayment = useCancelRegistrationPayment();
  const abandonPayment = useAbandonRegistrationPayment();
  const { data: capacity } = useEventCapacity(eventId);

  // Handicap check — auto-fires when position is set
  const {
    data: handicapCheck,
    isLoading: handicapCheckLoading,
  } = useHandicapCheck(eventId, formData.position);

  // Update a field
  const updateField = useCallback(
    <K extends keyof RegistrationFormData>(
      field: K,
      value: RegistrationFormData[K],
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear field error on change
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof ValidationErrors];
        return next;
      });
    },
    [],
  );

  // Reset manual handicap when position changes (new check will fire)
  useEffect(() => {
    setFormData((prev) => ({ ...prev, manual_handicap_value: null }));
  }, [formData.position]);

  // Validate current step
  const validateStep = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};
    // On-behalf flag: when set, the registration is for another buddy-group
    // member. The actor (primary cardholder) doesn't enter a partner or
    // a handicap on the target's behalf — those default from the target's
    // profile server-side, so we skip the corresponding form requirements.
    const isOnBehalf =
      !!formData.target_user_profile_id &&
      formData.target_user_profile_id !== '';

    switch (currentStep) {
      case 'partner':
        // Position (Header/Heeler) is only meaningful — and only required —
        // for team roping events. Solo disciplines skip the role picker.
        if (isTeamRoping && !formData.position) {
          newErrors.position = 'Please select Header or Heeler';
        }
        // Partner identification is mandatory for every team roping entry —
        // individual entries are not allowed. A REGISTERED partner is
        // required (a partner_user_profile_id from the search or buddy-group
        // picker); an off-platform email-only "partner" is NOT accepted,
        // because the backend persists no email/name for it and the entry
        // would land as a draw-eligible partnerless single. This applies to
        // self-registration and on-behalf flows alike, and mirrors the
        // server guard (PARTNER_REQUIRED_FOR_TEAM_ROPING).
        if (isTeamRoping && !formData.partner_user_profile_id) {
          newErrors.partner_email =
            'Pick a registered partner — search by name or choose a buddy group member. Your partner must have an account.';
        }
        // Performance preferences are now collected in the partner step
        // (see useRegistrationForm step filter — 'performance' is folded
        // in). Mirror the original 'performance' validation rules here.
        if (formData.first_performance_preference === 'Draw Out') {
          newErrors.first_performance_preference =
            '"Draw Out" cannot be your first preference';
        }
        if (
          formData.first_performance_preference &&
          formData.second_performance_preference &&
          formData.first_performance_preference ===
            formData.second_performance_preference
        ) {
          newErrors.first_performance_preference =
            'Second preference cannot match first preference';
        }
        break;
      case 'handicap': {
        const manualVal = formData.manual_handicap_value;
        // Skip the "needs handicap" gate for on-behalf registrations — the
        // backend will resolve the target user's existing handicap (or
        // create the registration without one if they don't have a handicap
        // on file yet; the contestant or an admin can add it later).
        if (!isOnBehalf && handicapCheck?.needs_handicap && manualVal == null) {
          newErrors.manual_handicap_value = 'Please enter your handicap number';
        }
        // Validate against event max handicap
        if (manualVal != null && formData.position) {
          const maxHC = getMaxHandicap(formData.position);
          if (maxHC != null && manualVal > maxHC) {
            newErrors.manual_handicap_value = `Handicap cannot exceed ${maxHC} for this event`;
          }
        }
        break;
      }
      case 'payment':
        // On-behalf path skips this step entirely (see steps filter above);
        // when the actor is registering themselves we still require a
        // payment method + membership choice.
        if (!isOnBehalf) {
          if (!isFreeEvent && !formData.payment_method) {
            newErrors.payment_method = 'Please select a payment method';
          }
          if (showMembershipChoice) {
            if (!formData.non_member_choice) {
              const hasSurcharge = (policyData?.non_member_surcharge ?? 0) > 0;
              newErrors.non_member_choice = hasSurcharge
                ? "Pick how you'd like to enter: pay the non-member fee or save your member number"
                : "Pick how you'd like to enter: continue as a non-member or save your member number";
            } else if (formData.non_member_choice === 'claim_member') {
              // The "Save member number" button on the panel persists the
              // claim into organization_memberships; on success the policy
              // refetch flips is_member=true and this whole block stops
              // rendering. If we're still showing the panel with the
              // claim_member choice selected, the user hasn't saved yet —
              // tell them to do that explicitly rather than letting them
              // try to submit a registration that the backend will reject.
              newErrors.non_member_choice =
                'Click "Save member number" to flag yourself as a member, then continue.';
            }
          }
        }
        break;
      case 'review':
        if (isTeamRoping && !formData.position) {
          newErrors.position = 'Position required';
        }
        if (!formData.event_id) newErrors.event_id = 'Event required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentStep, formData, isFreeEvent, handicapCheck, isTeamRoping, showMembershipChoice, policyData]);

  // Navigation
  const nextStep = useCallback(() => {
    if (!validateStep()) return;
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((i) => i + 1);
    }
  }, [currentStepIndex, validateStep, steps.length]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((i) => i - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback(
    (step: RegistrationStep) => {
      const idx = steps.indexOf(step);
      if (idx >= 0 && idx <= currentStepIndex) {
        setCurrentStepIndex(idx);
      }
    },
    [currentStepIndex, steps],
  );

  /**
   * Submit registration.
   *
   * Routes to the correct backend workflow based on payment method:
   * - Free events → direct registration via /registration-ops
   * - PayPal → /registration-payment-ops (returns approval_url)
   * - Cash → /cash-payment-ops
   *
   * Returns { success, action, paypalResponse } so the caller can
   * handle PayPal redirect.
   */
  const submit = useCallback(async (): Promise<{
    success: boolean;
    action?: string;
    error?: string;
    paypalResponse?: PaymentRegistrationResponse;
  }> => {
    if (!validateStep()) return { success: false };
    setSubmitError(null);

    // Rodeo registrations never carry a handicap. Even if the form had stale
    // state from an earlier (jackpot) session, we strip it here so the
    // wire payload matches what the rodeo backend path expects.
    const manualHandicap =
      !isRodeoEvent && handicapCheck?.needs_handicap && formData.manual_handicap_value != null
        ? formData.manual_handicap_value
        : undefined;

    // Position (Header/Heeler) is only sent for team roping events. Solo
    // disciplines submit without a role; backend stores position as NULL.
    const positionPayload = isTeamRoping
      ? { position: formData.position as 'Header' | 'Heeler' }
      : {};

    // On-behalf payload — only attached when the actor (primary) chose a
    // different buddy-group member. Always sent through the on-behalf
    // submit paths so the backend can re-authorize against the buddy
    // group + actor on every request (defense in depth).
    const onBehalfPayload = formData.target_user_profile_id
      ? { target_user_profile_id: formData.target_user_profile_id }
      : {};

    try {
      // On-behalf short-circuit: when the actor is registering another
      // member, the payment step is skipped entirely so the target picks
      // their own payment method later. We force pay_later here so the
      // spot is held in `pending_payment` for non-zero fees (the target
      // settles online from /payments) or `confirmed` for free events.
      // non_member_choice + claimed_member_number are omitted because
      // those belong to the target — the backend defaults appropriately
      // when they're absent.
      //
      // Partner selection IS supported on-behalf: the primary can pick a
      // specific partner for the buddy-group member, in which case
      // registration_type='team_primary' is forwarded along with
      // partner_user_profile_id / partner_email. The backend creates the
      // partner registration for the partner user (a separate person from
      // the target) the same way it does for self-registration.
      if (formData.target_user_profile_id) {
        await createRegistration.mutateAsync({
          event_id: formData.event_id,
          ...positionPayload,
          registration_type: formData.registration_type,
          partner_email: formData.partner_email || undefined,
          partner_user_profile_id: formData.partner_user_profile_id || undefined,
          ...buddyAndPreferencePayload,
          payment_method: 'pay_later',
          notes: formData.notes || undefined,
          ...onBehalfPayload,
        } as any);
        return { success: true, action: 'pay_later_pending' };
      }

      // Free events use the basic registration endpoint
      if (isFreeEvent) {
        const result: any = await createRegistration.mutateAsync({
          event_id: formData.event_id,
          ...positionPayload,
          registration_type: formData.registration_type,
          partner_email: formData.partner_email || undefined,
          partner_user_profile_id: formData.partner_user_profile_id || undefined,
          // Buddy group + performance preferences — accepted by backend but
          // not yet in generated types; omitted entirely on finals rodeos.
          ...buddyAndPreferencePayload,
          notes: formData.notes || undefined,
          manual_handicap_value: manualHandicap,
          // Card identity for priority system
          ...(formData.card_member_org_id ? { card_member_org_id: formData.card_member_org_id } : {}),
          ...(formData.card_member_user_id ? { card_member_user_id: formData.card_member_user_id } : {}),
          ...onBehalfPayload,
        } as any);
        return { success: true, action: result?.action };
      }

      // Stripe registration — handled via embedded Stripe Elements flow.
      // The submit() function is no longer used for online-pay — use
      // createStripeRegistration() and completeStripePayment() instead.
      // The legacy 'paypal' value is treated identically (backend coerces).
      if (
        formData.payment_method === 'stripe' ||
        formData.payment_method === 'paypal'
      ) {
        // Fallback: if submit() is called for the online-pay path, create
        // the registration but don't redirect. The caller should drive the
        // Stripe Elements flow from the returned client_secret.
        const result = await createStripeRegistration();
        return { success: true, action: 'stripe_embedded', paypalResponse: result };
      }

      // Cash registration — creates reg with pending_payment status
      if (formData.payment_method === 'cash') {
        const result = await registerWithCash.mutateAsync({
          event_id: formData.event_id,
          ...positionPayload,
          registration_type: formData.registration_type,
          partner_email: formData.partner_email || undefined,
          partner_user_profile_id: formData.partner_user_profile_id || undefined,
          paid_for_partner: formData.paid_for_partner || undefined,
          ...buddyAndPreferencePayload,
          notes: formData.notes || undefined,
          manual_handicap_value: manualHandicap,
          ...(formData.card_member_org_id ? { card_member_org_id: formData.card_member_org_id } : {}),
          ...(formData.card_member_user_id ? { card_member_user_id: formData.card_member_user_id } : {}),
          ...onBehalfPayload,
        } as any);
        return { success: true, action: result.status === 'pending_payment' ? 'cash_pending' : 'created' };
      }

      // Pay-later: spot is confirmed-as-pending — user settles online after draw
      if (formData.payment_method === 'pay_later') {
        await createRegistration.mutateAsync({
          event_id: formData.event_id,
          ...positionPayload,
          registration_type: formData.registration_type,
          partner_email: formData.partner_email || undefined,
          partner_user_profile_id: formData.partner_user_profile_id || undefined,
          paid_for_partner: formData.paid_for_partner || undefined,
          ...buddyAndPreferencePayload,
          payment_method: 'pay_later',
          notes: formData.notes || undefined,
          manual_handicap_value: manualHandicap,
          ...(formData.card_member_org_id ? { card_member_org_id: formData.card_member_org_id } : {}),
          ...(formData.card_member_user_id ? { card_member_user_id: formData.card_member_user_id } : {}),
          ...(formData.non_member_choice ? { non_member_choice: formData.non_member_choice } : {}),
          ...(formData.non_member_choice === 'claim_member' && formData.claimed_member_number.trim()
            ? { claimed_member_number: formData.claimed_member_number.trim() }
            : {}),
          ...onBehalfPayload,
        } as any);
        return { success: true, action: 'pay_later_pending' };
      }

      // Fallback — shouldn't happen but use basic registration
      const result: any = await createRegistration.mutateAsync({
        event_id: formData.event_id,
        ...positionPayload,
        registration_type: formData.registration_type,
        partner_email: formData.partner_email || undefined,
        partner_user_profile_id: formData.partner_user_profile_id || undefined,
        ...buddyAndPreferencePayload,
        payment_method: formData.payment_method || undefined,
        paid_for_partner: formData.paid_for_partner || undefined,
        notes: formData.notes || undefined,
        manual_handicap_value: manualHandicap,
        ...(formData.card_member_org_id ? { card_member_org_id: formData.card_member_org_id } : {}),
        ...(formData.card_member_user_id ? { card_member_user_id: formData.card_member_user_id } : {}),
        ...(formData.non_member_choice ? { non_member_choice: formData.non_member_choice } : {}),
        ...(formData.non_member_choice === 'claim_member' && formData.claimed_member_number.trim()
          ? { claimed_member_number: formData.claimed_member_number.trim() }
          : {}),
        ...onBehalfPayload,
      } as any);
      return { success: true, action: result?.action };
    } catch (err: any) {
      // Handle 402 UNPAID_BALANCE structured error from backend.
      // The HTTP client preserves the response body on `err.body`; we
      // also accept `err.response.data` for compatibility with any
      // legacy throw site that wraps the body that way.
      const errStatus = err?.status ?? err?.response?.status;
      const errBody = err?.body ?? err?.response?.data ?? err;
      // Members-only payment method rejection (403). This is a defensive
      // catch — the modal already disables the buttons for non-members —
      // but a stale form state, race during policy load, or an outdated
      // browser tab can still hit the gate. Surface a clear message and
      // bounce the user back to the payment step so they can re-pick.
      const isMembersOnly =
        err?.code === 'MEMBERS_ONLY_PAYMENT_METHOD' ||
        errBody?.code === 'MEMBERS_ONLY_PAYMENT_METHOD';
      if (isMembersOnly) {
        const errorMessage =
          (err instanceof Error && err.message) ||
          (errBody?.error as string | undefined) ||
          'Pay Later and Cash at Event are members-only. Pay online to hold your spot.';
        // Clear the rejected method so the user can choose another option.
        setFormData((prev) =>
          prev.payment_method === 'pay_later' || prev.payment_method === 'cash'
            ? { ...prev, payment_method: '' }
            : prev,
        );
        setSubmitError(errorMessage);
        return { success: false, error: errorMessage };
      }
      const isUnpaid =
        err?.code === 'UNPAID_BALANCE' ||
        errBody?.code === 'UNPAID_BALANCE' ||
        errStatus === 402;
      if (isUnpaid) {
        const unpaidData = errBody?.unpaid_events ?? err?.unpaid_events ?? [];
        const orgId = errBody?.organization_id ?? err?.organization_id ?? null;
        const orgName =
          errBody?.organization_name ?? err?.organization_name ?? null;
        if (unpaidData.length > 0 || orgId || orgName) {
          setOrgUnpaidBlock({
            organization_id: orgId,
            organization_name: orgName,
            unpaid_events: unpaidData,
          });
        }
        const errorMessage =
          (err instanceof Error && err.message) ||
          (errBody?.error as string | undefined) ||
          'You have outstanding unpaid fees. Please settle your balance before entering.';
        setSubmitError(errorMessage);
        return { success: false, error: errorMessage };
      }
      const errorMessage = err instanceof Error ? err.message : 'Entry failed';
      setSubmitError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [formData, validateStep, createRegistration, registerWithPayment, registerWithCash, handicapCheck, isFreeEvent, isTeamRoping, isRodeoEvent, buddyAndPreferencePayload]);

  /**
   * Create a Stripe registration (step 1 of embedded flow).
   * Calls the /register endpoint and returns the response including
   * stripe_payment_intent_id + client_secret. Does NOT redirect — the
   * caller mounts Stripe Elements with the client_secret.
   *
   * Catches the 402 UNPAID_BALANCE error inline so the per-org blocker
   * modal can be raised on this path too — the Stripe flow doesn't go
   * through `submit()`, so the catch block there never fires for
   * online-pay registrations.
   */
  const createStripeRegistration = useCallback(async (): Promise<PaymentRegistrationResponse> => {
    setSubmitError(null);

    const manualHandicap =
      !isRodeoEvent && handicapCheck?.needs_handicap && formData.manual_handicap_value != null
        ? formData.manual_handicap_value
        : undefined;

    // Stripe Elements doesn't redirect for the common card path; the URLs
    // are accepted by the backend for legacy compatibility but unused.
    const returnUrl = `${window.location.origin}/payments/success`;
    const cancelUrl = `${window.location.origin}/payments/cancel`;

    try {
      const result = await registerWithPayment.mutateAsync({
        event_id: formData.event_id,
        ...(isTeamRoping ? { position: formData.position as 'Header' | 'Heeler' } : {}),
        registration_type: formData.registration_type,
        partner_email: formData.partner_email || undefined,
        partner_user_profile_id: formData.partner_user_profile_id || undefined,
        paid_for_partner: formData.paid_for_partner || undefined,
        ...buddyAndPreferencePayload,
        notes: formData.notes || undefined,
        manual_handicap_value: manualHandicap,
        ...(formData.card_member_org_id ? { card_member_org_id: formData.card_member_org_id } : {}),
        ...(formData.card_member_user_id ? { card_member_user_id: formData.card_member_user_id } : {}),
        payment_method: 'stripe',
        return_url: returnUrl,
        cancel_url: cancelUrl,
      } as any);

      return result;
    } catch (err: any) {
      const errStatus = err?.status ?? err?.response?.status;
      const errBody = err?.body ?? err?.response?.data ?? err;
      const isUnpaid =
        err?.code === 'UNPAID_BALANCE' ||
        errBody?.code === 'UNPAID_BALANCE' ||
        errStatus === 402;
      if (isUnpaid) {
        const unpaidData = errBody?.unpaid_events ?? err?.unpaid_events ?? [];
        const orgId = errBody?.organization_id ?? err?.organization_id ?? null;
        const orgName =
          errBody?.organization_name ?? err?.organization_name ?? null;
        if (unpaidData.length > 0 || orgId || orgName) {
          setOrgUnpaidBlock({
            organization_id: orgId,
            organization_name: orgName,
            unpaid_events: unpaidData,
          });
        }
        // Re-throw a typed error so the PayPalButtons callback knows the
        // create-order step failed AND the parent component can branch
        // on `code === 'UNPAID_BALANCE'` to suppress the generic
        // "Registration Error" toast (the modal carries the message).
        const enriched = new Error(
          (err instanceof Error && err.message) ||
            (errBody?.error as string | undefined) ||
            'Outstanding balance — settle before entering',
        ) as Error & { code?: string };
        enriched.code = 'UNPAID_BALANCE';
        throw enriched;
      }
      throw err;
    }
  }, [formData, handicapCheck, registerWithPayment, isTeamRoping, isRodeoEvent, buddyAndPreferencePayload]);

  /**
   * Complete a Stripe payment (step 2 of embedded flow).
   * Called from the StripeCheckoutDialog onApprove callback after Stripe
   * Elements has confirmed the PaymentIntent. The webhook is the source
   * of truth; this is the optimistic fast path.
   */
  const completeStripePayment = useCallback(async (
    registrationId: string,
    paymentIntentId: string,
  ) => {
    return completePayment.mutateAsync({
      registrationId,
      payment_intent_id: paymentIntentId,
    });
  }, [completePayment]);

  /**
   * Cancel a pending Stripe registration when the dialog is dismissed
   * before payment confirms. Cleans up the pending registration on the
   * backend.
   */
  const cancelStripeRegistration = useCallback(async (registrationId: string) => {
    return cancelPayment.mutateAsync(registrationId);
  }, [cancelPayment]);

  /**
   * Fully purge a Stripe registration that was created during checkout
   * but never paid. Use this in place of cancelStripeRegistration when
   * the user backs out of the embedded Stripe flow — `abandon` deletes
   * the row entirely so it doesn't show up as a "cancelled" entry in
   * their funds list. Backend gates by status + payment status so it's
   * safe to call: it'll error out if money has actually moved.
   */
  const abandonStripeRegistration = useCallback(async (registrationId: string) => {
    return abandonPayment.mutateAsync(registrationId);
  }, [abandonPayment]);

  // Reset
  const reset = useCallback(() => {
    setFormData({
      event_id: eventId,
      position: '',
      registration_type: 'individual',
      partner_email: '',
      partner_user_profile_id: '',
      buddy_group_id: '',
      buddy_group_position: null,
      payment_method: '',
      paid_for_partner: false,
      notes: '',
      manual_handicap_value: null,
      first_performance_preference: '',
      second_performance_preference: '',
      card_member_org_id: '',
      card_member_user_id: '',
      non_member_choice: '',
      claimed_member_number: '',
      target_user_profile_id: '',
    });
    setCurrentStepIndex(0);
    setErrors({});
    setSubmitError(null);
    setOrgUnpaidBlock(null);
  }, [eventId]);

  /** Allow callers to dismiss the org-scoped balance modal explicitly. */
  const clearOrgUnpaidBlock = useCallback(() => {
    setOrgUnpaidBlock(null);
  }, []);

  return {
    // State
    formData,
    currentStep,
    currentStepIndex,
    steps,
    errors,
    submitError,
    isSubmitting: createRegistration.isPending || registerWithPayment.isPending || registerWithCash.isPending || completePayment.isPending,
    capacity,
    handicapCheck: handicapCheck ?? null,
    handicapCheckLoading,
    maxHandicap: formData.position ? getMaxHandicap(formData.position) : null,
    totalFee,
    surcharge,
    /**
     * Resolved per-side transaction fee. Honors a non-zero per-event
     * override; otherwise derives from the platform Stripe fee config so
     * auto-mode events (transaction_fee = 0/NULL) surface the fee Stripe
     * will actually charge. The wizard's fee summary + Stripe step both
     * read this so the displayed total stays in lock-step with what the
     * backend bills.
     */
    effectiveTransactionFee,
    /**
     * Partner's actual per-side cost (membership-aware). Equals
     * `totalFee` until a partner is selected and the preview loads;
     * after that it reflects the partner's real fee so the review
     * step's "Partner fee" / "Total due" lines match what Stripe
     * Elements will charge.
     */
    partnerTotalFee,
    /** Raw partner fee preview — exposes is_member, surcharge, org name. */
    partnerFeePreview,
    /**
     * Membership-aware fee preview for the on-behalf target
     * (target_user_profile_id). Null when registering for self or
     * before the lookup resolves. The review step uses this to hide
     * the "non-member fee" hint when the target is actually a member.
     */
    targetFeePreview,
    isFreeEvent,
    isTeamRoping,
    policyData,
    hasUnpaidBalance,
    cashAllowed,
    payLaterAllowed,
    unpaidEvents,
    unpaidFeeBlock,
    /**
     * Per-org registration block returned by the backend's 402
     * UNPAID_BALANCE response. Non-null means: "the user owes the
     * org that owns this event's rodeo for past-due fees and must
     * settle before registering for this event." Drives the modal
     * shown in RegistrationModal.
     */
    orgUnpaidBlock,

    // Actions
    updateField,
    nextStep,
    prevStep,
    goToStep,
    submit,
    createStripeRegistration,
    completeStripePayment,
    cancelStripeRegistration,
    abandonStripeRegistration,
    // Legacy aliases for the rolling rename. Remove once every caller is
    // off the PayPal-flavored names.
    createPayPalRegistration: createStripeRegistration,
    completePayPalPayment: completeStripePayment,
    cancelPayPalRegistration: cancelStripeRegistration,
    abandonPayPalRegistration: abandonStripeRegistration,
    clearOrgUnpaidBlock,
    reset,
  };
}
